import { useState } from 'react';
import { useIntl } from 'react-intl';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { forceTimeString, toMoment, weekDateFormat } from 'src/utils/datetime';
import { colors } from 'src/utils/customColors';
import { useGetDispoTimeWindows } from 'src/pages/ProjectDetailsPage/api';
import messages from '../../messages';

interface Props {
  jobUuid: string;
  date: string;
  appliedCount: number;
  onSelect: (date: string, jobDateId: string) => void;
}

export default function DispoDayInfo({
  jobUuid,
  date,
  appliedCount,
  onSelect,
}: Props) {
  const { formatMessage } = useIntl();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const {
    data: timeWindows,
    isPending,
    isError,
  } = useGetDispoTimeWindows({
    jobUuid,
    shiftDate: date,
    enabled: open,
  });
  const windows = (timeWindows ?? [])
    .filter(({ id }) => id)
    .sort((first, second) =>
      (first.startsAt ?? '').localeCompare(second.startsAt ?? ''),
    );

  return (
    <>
      <Typography
        fontSize={10}
        lineHeight="16px"
        bgcolor={colors.lightYellow}
        color={colors.lightBrown}
        borderRadius={1}
        px={0.5}
        display="inline-block"
        maxWidth="100%"
        noWrap
        aria-label={formatMessage(messages.applicationsCountLabel, {
          count: appliedCount,
        })}
      >
        {formatMessage(messages.appliedCountShortLabel, {
          count: appliedCount,
        })}
      </Typography>
      <IconButton
        size="small"
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          p: 0.25,
          color: colors.darkerGrey,
        }}
        aria-label={formatMessage(messages.timeWindowsLabel, { date })}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 14 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2 } } }}
      >
        <Box
          p={1}
          width={264}
          role="dialog"
          aria-label={formatMessage(messages.timeWindowsLabel, { date })}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            pl={1}
            mb={0.5}
          >
            <Typography fontSize={12} fontWeight={600}>
              {toMoment(date)?.format(weekDateFormat)}
            </Typography>
            <IconButton
              size="small"
              aria-label={formatMessage(messages.closeTimeWindowsLabel)}
              onClick={() => setAnchorEl(null)}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
          {isPending && (
            <Box p={1} display="flex" justifyContent="center">
              <CircularProgress
                size={20}
                aria-label={formatMessage(messages.timeWindowsLoadingLabel)}
              />
            </Box>
          )}
          {isError && (
            <Typography p={1} fontSize={12} color="error" role="alert">
              {formatMessage(messages.timeWindowsErrorLabel)}
            </Typography>
          )}
          <MenuList dense disablePadding>
            {!isError &&
              windows.map((window) => {
                const time = `${forceTimeString(window.startsAt)} - ${forceTimeString(window.endsAt)}`;
                const count = formatMessage(messages.applicationsCountLabel, {
                  count: window.applicationsCount,
                });
                return (
                  <MenuItem
                    key={window.id}
                    aria-label={`${time} · ${count}`}
                    sx={{
                      justifyContent: 'space-between',
                      gap: 2,
                      px: 1,
                      py: 0.5,
                    }}
                    onClick={() => {
                      onSelect(date, window.id.toString());
                      setAnchorEl(null);
                    }}
                  >
                    <Typography fontSize={12}>{time}</Typography>
                    <Typography fontSize={12} color={colors.lightBrown}>
                      {count}
                    </Typography>
                  </MenuItem>
                );
              })}
          </MenuList>
        </Box>
      </Popover>
    </>
  );
}
